import React, { useState, useEffect } from 'react';
import {
  Brain,
  Phone,
  Plus,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  PhoneOff,
  RefreshCw,
  FileText,
  Search,
  Filter,
  Video,
  Hash,
  Save
} from 'lucide-react';
import { callsAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import clsx from 'clsx';

const TechnicalExpertPage = () => {
  const [activeTab, setActiveTab] = useState('consultations'); // consultations, history
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Consultation form state
  const [consultationForm, setConsultationForm] = useState({
    callType: 'phone', // 'phone', 'zoom', or 'teams'
    toNumber: '',
    zoomLink: '',  // Full Zoom meeting link for easy parsing
    zoomMeetingId: '',
    zoomPin: '',
    teamsMeetingId: '',
    fromPhoneNumberId: '',
    notes: '',
    meetingContext: ''
  });

  // Parse Zoom meeting link to extract meeting ID and passcode
  // Supports formats like:
  // - https://zoom.us/j/1234567890?pwd=abc123
  // - https://us02web.zoom.us/j/1234567890?pwd=abc123
  // - 1234567890 (just the meeting ID)
  const parseZoomLink = (link) => {
    if (!link) return { meetingId: '', passcode: '' };

    // If it's just a number, treat it as meeting ID
    if (/^\d+$/.test(link.trim())) {
      return { meetingId: link.trim(), passcode: '' };
    }

    try {
      // Try to parse as URL
      const url = new URL(link);
      const pathParts = url.pathname.split('/');
      const meetingId = pathParts.find(part => /^\d{9,11}$/.test(part)) || '';
      const passcode = url.searchParams.get('pwd') || '';
      return { meetingId, passcode };
    } catch (e) {
      // Not a valid URL, try regex extraction
      const meetingIdMatch = link.match(/\/j\/(\d{9,11})/);
      const passcodeMatch = link.match(/pwd=([a-zA-Z0-9]+)/);
      return {
        meetingId: meetingIdMatch ? meetingIdMatch[1] : '',
        passcode: passcodeMatch ? passcodeMatch[1] : ''
      };
    }
  };

  // Handle Zoom link change - auto-parse and fill meeting ID and passcode
  const handleZoomLinkChange = (e) => {
    const link = e.target.value;
    const { meetingId, passcode } = parseZoomLink(link);
    setConsultationForm(prev => ({
      ...prev,
      zoomLink: link,
      zoomMeetingId: meetingId || prev.zoomMeetingId,
      zoomPin: passcode || prev.zoomPin
    }));
  };

  // State for save feedback
  const [saveMessage, setSaveMessage] = useState(null);

  // Local storage keys - separate preferences from per-call data
  const PREFERENCES_KEY = 'technical_expert_preferences';  // Persists: fromPhoneNumberId, callType
  const CALL_DATA_KEY = 'technical_expert_call_data';      // Saved manually, cleared after call

  // Load saved preferences on mount (these persist across sessions)
  const loadPreferences = () => {
    try {
      const saved = localStorage.getItem(PREFERENCES_KEY);
      if (saved) {
        const prefs = JSON.parse(saved);
        setConsultationForm(prev => ({
          ...prev,
          fromPhoneNumberId: prefs.fromPhoneNumberId || prev.fromPhoneNumberId,
          callType: prefs.callType || prev.callType
        }));
        return true;
      }
    } catch (error) {
      console.error('Failed to load preferences:', error);
    }
    return false;
  };

  // Auto-save preferences when they change
  const savePreferences = (prefs) => {
    try {
      localStorage.setItem(PREFERENCES_KEY, JSON.stringify({
        fromPhoneNumberId: prefs.fromPhoneNumberId,
        callType: prefs.callType
      }));
    } catch (error) {
      console.error('Failed to save preferences:', error);
    }
  };

  // Load per-call data (Zoom info, notes, etc.) from localStorage
  const loadSavedFormData = () => {
    try {
      const saved = localStorage.getItem(CALL_DATA_KEY);
      if (saved) {
        const parsedData = JSON.parse(saved);
        setConsultationForm(prev => ({
          ...prev,
          ...parsedData
        }));
        return true;
      }
    } catch (error) {
      console.error('Failed to load saved form data:', error);
    }
    return false;
  };

  // Save per-call form data to localStorage (manual save for meeting details, notes)
  const handleSaveForm = () => {
    try {
      // Save call-specific data (not preferences)
      const callData = {
        toNumber: consultationForm.toNumber,
        zoomLink: consultationForm.zoomLink,
        zoomMeetingId: consultationForm.zoomMeetingId,
        zoomPin: consultationForm.zoomPin,
        teamsMeetingId: consultationForm.teamsMeetingId,
        notes: consultationForm.notes,
        meetingContext: consultationForm.meetingContext
      };
      localStorage.setItem(CALL_DATA_KEY, JSON.stringify(callData));
      setSaveMessage({ type: 'success', text: 'Form data saved!' });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error('Failed to save form data:', error);
      setSaveMessage({ type: 'error', text: 'Failed to save form data' });
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  // Clear only per-call data (preferences are kept)
  const clearSavedFormData = () => {
    try {
      localStorage.removeItem(CALL_DATA_KEY);
    } catch (error) {
      console.error('Failed to clear saved form data:', error);
    }
  };

  useEffect(() => {
    loadPhoneNumbers();
    loadConsultationHistory();
    loadPreferences();  // Load saved preferences (phone number, call type) on mount
  }, []);

  // Auto-save preferences when callType or fromPhoneNumberId changes
  useEffect(() => {
    if (consultationForm.fromPhoneNumberId || consultationForm.callType !== 'phone') {
      savePreferences(consultationForm);
    }
  }, [consultationForm.fromPhoneNumberId, consultationForm.callType]);

  // Load saved form data when modal opens
  useEffect(() => {
    if (showCreateModal) {
      const hasData = loadSavedFormData();
      if (hasData) {
        setSaveMessage({ type: 'info', text: 'Previously saved meeting details loaded' });
        setTimeout(() => setSaveMessage(null), 3000);
      }
    }
  }, [showCreateModal]);

  const loadPhoneNumbers = async () => {
    try {
      const response = await callsAPI.getPhoneNumbers();
      setPhoneNumbers(response.data.data || []);
    } catch (error) {
      console.error('Failed to load phone numbers:', error);
    }
  };

  const loadConsultationHistory = async () => {
    try {
      setLoading(true);
      // Get calls with technical_expert metadata
      const response = await callsAPI.getCalls({
        limit: 50,
        metadata_source: 'technical_expert_consultation'
      });
      setConsultations(response.data.data?.calls || []);
    } catch (error) {
      console.error('Failed to load consultation history:', error);
      setConsultations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateConsultation = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      // Validate based on call type
      if (consultationForm.callType === 'phone') {
        if (!consultationForm.toNumber) {
          alert('Please enter a phone number to call');
          return;
        }
      } else if (consultationForm.callType === 'zoom') {
        if (!consultationForm.zoomMeetingId) {
          alert('Please enter a Zoom meeting ID');
          return;
        }
        if (!consultationForm.zoomPin) {
          alert('Please enter a Zoom PIN');
          return;
        }
      } else if (consultationForm.callType === 'teams') {
        if (!consultationForm.teamsMeetingId) {
          alert('Please enter a Teams meeting ID');
          return;
        }
      }

      if (!consultationForm.fromPhoneNumberId) {
        alert('Please select a phone number to call from');
        return;
      }

      // Find the selected phone number record to get the actual phone number string
      const selectedPhoneNumber = phoneNumbers.find(p => p.id === consultationForm.fromPhoneNumberId);
      if (!selectedPhoneNumber) {
        alert('Selected phone number not found');
        return;
      }

      // Prepare call data based on call type
      let toNumber = consultationForm.toNumber;
      let greeting = "Hello! This is your AI technical expert. I'm here to answer any questions about our products and services. How can I help you today?";

      // For Zoom/Teams, construct the dial-in number with DTMF tones
      if (consultationForm.callType === 'zoom') {
        // Zoom dial-in sequence:
        // 1. Dial Zoom number
        // 2. Wait for "Please enter meeting ID" (~10s with new backend timing)
        // 3. Enter MEETINGID#
        // 4. Wait for "Please enter participant ID or # to skip" (~6s)
        // 5. Enter # to skip participant ID
        // 6. Wait for "Please enter passcode" (~4s)
        // 7. Enter PIN#
        // Format: +16699009128,,MEETINGID#,,#,,PIN#
        toNumber = `+16699009128,,${consultationForm.zoomMeetingId}#,,#,,${consultationForm.zoomPin}#`;
        greeting = `Hello! This is Alex, your AI technical expert. I'm joining the Zoom meeting now. Please hold while I connect.`;
      } else if (consultationForm.callType === 'teams') {
        // Teams dial-in: Call Teams number, wait, enter conference ID
        // Format: +12133386000,,CONFERENCEID#
        toNumber = `+12133386000,,${consultationForm.teamsMeetingId}#`;
        greeting = `Hello! This is Alex, your AI technical expert. I'm joining the Teams meeting now. Please hold while I connect.`;
      }

      // Create the consultation call
      const response = await callsAPI.createOutboundCall({
        toNumber: toNumber,
        fromNumber: selectedPhoneNumber.phoneNumber, // Use the phone number string, not ID
        introMessage: greeting,
        voiceModel: 'coral', // Friendly voice for expert mode
        questions: [], // No questions for expert mode, it's conversational
        metadata: {
          aiMode: 'technical_expert', // ⚠️ CRITICAL: Activates expert mode
          source: 'technical_expert_consultation',
          callType: consultationForm.callType,
          callPurpose: 'Technical consultation and product knowledge assistance',
          notes: consultationForm.notes,
          meetingContext: consultationForm.meetingContext,
          ...(consultationForm.callType === 'zoom' && {
            zoomMeetingId: consultationForm.zoomMeetingId,
            zoomPin: consultationForm.zoomPin
          }),
          ...(consultationForm.callType === 'teams' && {
            teamsMeetingId: consultationForm.teamsMeetingId
          })
        }
      });

      alert(`Technical expert consultation initiated successfully via ${consultationForm.callType.toUpperCase()}!`);

      // Clear per-call data (meeting details, notes) but keep preferences
      clearSavedFormData();

      // Reset per-call fields but preserve preferences (callType, fromPhoneNumberId)
      setConsultationForm(prev => ({
        callType: prev.callType,                    // Keep preferred call type
        toNumber: '',
        zoomLink: '',
        zoomMeetingId: '',
        zoomPin: '',
        teamsMeetingId: '',
        fromPhoneNumberId: prev.fromPhoneNumberId,  // Keep preferred phone number
        notes: '',
        meetingContext: ''
      }));
      setShowCreateModal(false);

      // Reload consultation history
      loadConsultationHistory();

    } catch (error) {
      console.error('Failed to create consultation:', error);
      alert('Failed to initiate consultation: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'Unknown';
    // Format +15125551234 as (512) 555-1234
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      const areaCode = cleaned.substring(1, 4);
      const prefix = cleaned.substring(4, 7);
      const lineNumber = cleaned.substring(7);
      return `(${areaCode}) ${prefix}-${lineNumber}`;
    }
    return phone;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Completed' },
      in_progress: { bg: 'bg-blue-100', text: 'text-blue-800', icon: Phone, label: 'In Progress' },
      failed: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, label: 'Failed' },
      no_answer: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: PhoneOff, label: 'No Answer' }
    };

    const config = statusConfig[status] || statusConfig.completed;
    const Icon = config.icon;

    return (
      <span className={clsx('inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium', config.bg, config.text)}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const filteredConsultations = consultations.filter(consultation => {
    const searchLower = searchTerm.toLowerCase();
    return (
      consultation.callerNumber?.toLowerCase().includes(searchLower) ||
      consultation.metadata?.notes?.toLowerCase().includes(searchLower) ||
      consultation.metadata?.meetingContext?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-orange-500 rounded-xl flex items-center justify-center">
              <Brain className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Technical Expert Consultations</h1>
              <p className="text-gray-600 mt-1">AI-powered technical expertise on demand</p>
            </div>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-orange-500 text-white px-6 py-3 rounded-lg font-medium hover:shadow-lg transition-shadow"
          >
            <Plus className="w-5 h-5" />
            New Consultation
          </button>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Brain className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Consultations</p>
                <p className="text-2xl font-bold text-gray-900">{consultations.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Successful</p>
                <p className="text-2xl font-bold text-gray-900">
                  {consultations.filter(c => c.status === 'completed').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Avg Duration</p>
                <p className="text-2xl font-bold text-gray-900">
                  {consultations.length > 0
                    ? formatDuration(Math.round(consultations.reduce((sum, c) => sum + (c.durationSeconds || 0), 0) / consultations.length))
                    : '0s'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="flex gap-4 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('consultations')}
            className={clsx(
              'px-4 py-2 font-medium transition-colors border-b-2',
              activeTab === 'consultations'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            )}
          >
            Consultation History
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search consultations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Consultation History */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : filteredConsultations.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <Brain className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Consultations Yet</h3>
          <p className="text-gray-600 mb-6">
            {searchTerm
              ? 'No consultations match your search.'
              : 'Start your first technical expert consultation to get AI-powered product knowledge and support.'}
          </p>
          {!searchTerm && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-orange-500 text-white px-6 py-3 rounded-lg font-medium hover:shadow-lg transition-shadow"
            >
              <Plus className="w-5 h-5" />
              New Consultation
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phone Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notes
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredConsultations.map((consultation) => (
                <tr key={consultation.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(consultation.startedAt).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatPhoneNumber(consultation.callerNumber)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {formatDuration(consultation.durationSeconds)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(consultation.status)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                    {consultation.metadata?.notes || consultation.metadata?.meetingContext || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Consultation Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-orange-500 rounded-lg flex items-center justify-center">
                    <Brain className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">New Technical Expert Consultation</h2>
                </div>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleCreateConsultation} className="p-6 space-y-6">
              {/* Call Type Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Call Type *
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setConsultationForm({ ...consultationForm, callType: 'phone' })}
                    className={clsx(
                      'flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all',
                      consultationForm.callType === 'phone'
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <Phone className="w-6 h-6" />
                    <span className="text-sm font-medium">Phone Call</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setConsultationForm({ ...consultationForm, callType: 'zoom' })}
                    className={clsx(
                      'flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all',
                      consultationForm.callType === 'zoom'
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <Video className="w-6 h-6" />
                    <span className="text-sm font-medium">Zoom</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setConsultationForm({ ...consultationForm, callType: 'teams' })}
                    className={clsx(
                      'flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all',
                      consultationForm.callType === 'teams'
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <Hash className="w-6 h-6" />
                    <span className="text-sm font-medium">Teams</span>
                  </button>
                </div>
              </div>

              {/* Conditional Fields Based on Call Type */}
              {consultationForm.callType === 'phone' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number to Call *
                  </label>
                  <input
                    type="tel"
                    required
                    value={consultationForm.toNumber}
                    onChange={(e) => setConsultationForm({ ...consultationForm, toNumber: e.target.value })}
                    placeholder="+15125551234"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Enter in E.164 format (e.g., +15125551234)
                  </p>
                </div>
              )}

              {consultationForm.callType === 'zoom' && (
                <>
                  {/* Zoom Link - Easy paste option */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <label className="block text-sm font-medium text-blue-800 mb-2">
                      Paste Zoom Meeting Link (Recommended)
                    </label>
                    <input
                      type="text"
                      value={consultationForm.zoomLink || ''}
                      onChange={handleZoomLinkChange}
                      placeholder="https://zoom.us/j/1234567890?pwd=abc123"
                      className="w-full px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="mt-1 text-sm text-blue-600">
                      Paste your full Zoom link to auto-fill the Meeting ID and Passcode below
                    </p>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <p className="text-sm text-gray-500 mb-3">Or enter manually:</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Zoom Meeting ID *
                    </label>
                    <input
                      type="text"
                      required
                      value={consultationForm.zoomMeetingId}
                      onChange={(e) => setConsultationForm({ ...consultationForm, zoomMeetingId: e.target.value })}
                      placeholder="123 456 7890"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Enter the Zoom meeting ID (numbers only)
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Zoom Passcode *
                    </label>
                    <input
                      type="text"
                      required
                      value={consultationForm.zoomPin}
                      onChange={(e) => setConsultationForm({ ...consultationForm, zoomPin: e.target.value })}
                      placeholder="123456"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Enter the meeting passcode (from the meeting invite)
                    </p>
                  </div>
                </>
              )}

              {consultationForm.callType === 'teams' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Teams Conference ID *
                  </label>
                  <input
                    type="text"
                    required
                    value={consultationForm.teamsMeetingId}
                    onChange={(e) => setConsultationForm({ ...consultationForm, teamsMeetingId: e.target.value })}
                    placeholder="123 456 789#"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Enter the Teams conference ID
                  </p>
                </div>
              )}

              {/* From Phone Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Call From Phone Number *
                </label>
                <select
                  required
                  value={consultationForm.fromPhoneNumberId}
                  onChange={(e) => setConsultationForm({ ...consultationForm, fromPhoneNumberId: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select a phone number...</option>
                  {phoneNumbers.map((phone) => (
                    <option key={phone.id} value={phone.id}>
                      {phone.phoneNumber} {phone.nickname ? `(${phone.nickname})` : ''}
                    </option>
                  ))}
                </select>
                {consultationForm.callType !== 'phone' && (
                  <p className="mt-1 text-sm text-gray-500">
                    This number will dial into the {consultationForm.callType === 'zoom' ? 'Zoom' : 'Teams'} meeting
                  </p>
                )}
              </div>

              {/* Meeting Context */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meeting/Consultation Context
                </label>
                <input
                  type="text"
                  value={consultationForm.meetingContext}
                  onChange={(e) => setConsultationForm({ ...consultationForm, meetingContext: e.target.value })}
                  placeholder="e.g., Product demo for ABC Company"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={consultationForm.notes}
                  onChange={(e) => setConsultationForm({ ...consultationForm, notes: e.target.value })}
                  rows={3}
                  placeholder="Optional notes about this consultation..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Expert Mode Features */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h3 className="font-semibold text-purple-900 mb-2">🎓 Expert Mode Features</h3>
                <ul className="space-y-1 text-sm text-purple-800">
                  <li>✓ Knowledge base documents emphasized</li>
                  <li>✓ Extremely friendly communication style</li>
                  <li>✓ Slower speech pacing for clarity</li>
                  <li>✓ Technical explanations in simple terms</li>
                </ul>
              </div>

              {/* Save Message Feedback */}
              {saveMessage && (
                <div className={clsx(
                  'flex items-center gap-2 p-3 rounded-lg text-sm',
                  saveMessage.type === 'success' && 'bg-green-50 text-green-800 border border-green-200',
                  saveMessage.type === 'error' && 'bg-red-50 text-red-800 border border-red-200',
                  saveMessage.type === 'info' && 'bg-blue-50 text-blue-800 border border-blue-200'
                )}>
                  {saveMessage.type === 'success' && <CheckCircle className="w-4 h-4" />}
                  {saveMessage.type === 'error' && <XCircle className="w-4 h-4" />}
                  {saveMessage.type === 'info' && <Save className="w-4 h-4" />}
                  {saveMessage.text}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveForm}
                  className="flex items-center gap-2 px-6 py-3 border border-purple-300 text-purple-700 bg-purple-50 rounded-lg font-medium hover:bg-purple-100 transition-colors"
                  disabled={loading}
                >
                  <Save className="w-4 h-4" />
                  Save
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-purple-600 to-orange-500 text-white px-6 py-3 rounded-lg font-medium hover:shadow-lg transition-shadow disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Initiating...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Phone className="w-4 h-4" />
                      Start Consultation
                    </span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TechnicalExpertPage;
